import type { NavigateFunction } from 'react-router-dom';
import kebabCase from 'lodash/kebabCase';

import type { DropdownOption } from '@tcl-models/models';
import { ALL_SITES, type PatientCarePathwayDtoType } from '@tempus/trials-types';
import { PATIENT_PAGE_TAB } from '~/store/patient/constants';
import {
  getTimePrescreeningPageUrl,
  PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY,
  PATIENT_WORKSPACE_CRITERIA_TRIAL_ID_QUERY,
  PATIENT_WORKSPACE_EVALUATION_MODE_QUERY,
  type PatientWorkspaceScreeningListReturn,
  type TimePrescreeningPageUrlOptions,
} from '~/routes/paths';
import {
  decodePatientWorkspaceOrigin,
  encodePatientWorkspaceOrigin,
  getCarePathwayScreeningLocation,
} from '~/routes/patientWorkspaceOrigin';
import { storeToParams } from '~/routes/timeScreeningPage/matchingCarePathways/matchingCarePathways.helpers';
import type { TIMEScreeningState } from '~/store/api/types/timeScreeningStore';
import { MatchingTab } from '~/store/timeScreening/constants';
import { buildPatientWorkspaceSearchParamsExcludingEvalId } from './patientWorkspaceUrl.helpers';
import type { PatientWorkspaceEvaluationMode } from './PatientWorkspacePage/patientWorkspacePage.types';

export type PatientWorkspaceUrlContext = {
  carePathwayEvaluationId?: string;
  /** Care pathway type id (queue `cpQueuePathwayId`) — filters tracking feed when prefetching. */
  carePathwayTypeId?: string;
  searchParams?: TimePrescreeningPageUrlOptions['searchParams'];
};

/** Builds `/patient/:linkage/:institution/:tab` with optional evaluation path segment and queue query params. */
export function buildPatientWorkspaceUrl(
  patientLinkageId: string,
  institutionId: string,
  tab: PATIENT_PAGE_TAB,
  context?: PatientWorkspaceUrlContext,
): string | null {
  const linkage = patientLinkageId?.trim();
  const institution = institutionId?.trim();
  if (!linkage || !institution) {
    return null;
  }

  const carePathwayTypeId = context?.carePathwayTypeId?.trim();
  const url = getTimePrescreeningPageUrl(linkage, institution, tab, {
    carePathwayEvaluationId: context?.carePathwayEvaluationId?.trim(),
    searchParams: {
      ...context?.searchParams,
      ...(carePathwayTypeId ? { [PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY]: carePathwayTypeId } : {}),
    },
  });

  return url === '/patient' ? null : url;
}

/** In-tab navigation: preserves query params and strips the legacy evaluation id query key. */
export function buildPatientWorkspaceTabUrl(
  patientLinkageId: string,
  institutionId: string,
  tab: PATIENT_PAGE_TAB,
  locationSearch = '',
  carePathwayEvaluationId?: string,
): string {
  return getTimePrescreeningPageUrl(patientLinkageId, institutionId, tab, {
    carePathwayEvaluationId,
    searchParams: buildPatientWorkspaceSearchParamsExcludingEvalId(locationSearch),
  });
}

/** Evaluation tab URL that selects the given trial in criteria groups. */
export function buildPatientWorkspaceEvaluationHref(
  patientLinkageId: string,
  institutionId: string,
  trialId: string,
  locationSearch = '',
  carePathwayEvaluationId?: string,
  evaluationMode: PatientWorkspaceEvaluationMode = 'trial',
): string {
  const url = buildPatientWorkspaceTabUrl(
    patientLinkageId,
    institutionId,
    PATIENT_PAGE_TAB.EVALUATION,
    locationSearch,
    carePathwayEvaluationId,
  );
  const trimmedTrialId = trialId.trim();
  if (!trimmedTrialId) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  const path = queryIndex === -1 ? url : url.slice(0, queryIndex);
  const params = new URLSearchParams(queryIndex === -1 ? '' : url.slice(queryIndex + 1));
  params.set(PATIENT_WORKSPACE_CRITERIA_TRIAL_ID_QUERY, trimmedTrialId);
  params.set(PATIENT_WORKSPACE_EVALUATION_MODE_QUERY, evaluationMode);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export type SiteTrialPatientEvaluationHrefOptions = {
  carePathwayEvaluationId?: string;
  /** Site-trial queue tab, restored by the patient workspace breadcrumb. */
  siteTrialTab?: string;
};

/** Site-trial row link: evaluation tab, criteria trial id, site-trial origin, optional care-pathway evaluation segment. */
export function buildSiteTrialPatientEvaluationHref(
  patientLinkageId: string,
  institutionId: string,
  trialId: string,
  options?: SiteTrialPatientEvaluationHrefOptions,
): string | undefined {
  const linkageId = patientLinkageId.trim();
  const institution = institutionId.trim();
  const trimmedTrialId = trialId.trim();
  if (!linkageId || !institution || !trimmedTrialId) {
    return undefined;
  }

  const originSearch = new URLSearchParams(
    encodePatientWorkspaceOrigin({ kind: 'siteTrial', trialId: trimmedTrialId, tab: options?.siteTrialTab }),
  ).toString();
  const trimmedEvaluationId = options?.carePathwayEvaluationId?.trim();
  return buildPatientWorkspaceEvaluationHref(
    linkageId,
    institution,
    trimmedTrialId,
    originSearch,
    trimmedEvaluationId || undefined,
  );
}

export type CarePathwayRowNavigationContext = {
  /** Institution id from the matching/screened table row (preferred). */
  rowSiteId?: string | null;
  /** Care-gap filter sites (use institution id when exactly one site is selected). */
  filterSites: DropdownOption[];
  /** Global site selector fallback when filter sites are ambiguous. */
  selectedUserSiteId?: string | null;
};

export function isResolvableInstitutionId(siteId: string | null | undefined): siteId is string {
  const trimmed = siteId?.trim();
  return Boolean(trimmed && trimmed !== ALL_SITES);
}

/** Picks the first valid Salesforce institution id from workspace/route/metadata sources. */
export function resolveWorkspacePatientSiteId(...candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    if (isResolvableInstitutionId(candidate)) {
      return candidate.trim();
    }
  }

  return undefined;
}

/**
 * Resolves Salesforce `institutionId` for `/patient/:linkage/:institution/...` from matching-table context.
 * Returns undefined when institution cannot be determined reliably.
 */
export function resolveInstitutionIdForCarePathwayRow({
  rowSiteId,
  filterSites,
  selectedUserSiteId,
}: CarePathwayRowNavigationContext): string | undefined {
  if (isResolvableInstitutionId(rowSiteId)) {
    return rowSiteId.trim();
  }

  if (filterSites.length === 1) {
    const siteId = filterSites[0]?.value;
    if (siteId != null && isResolvableInstitutionId(String(siteId))) {
      return String(siteId).trim();
    }
  }

  if (isResolvableInstitutionId(selectedUserSiteId)) {
    return selectedUserSiteId.trim();
  }

  return undefined;
}

export type BuildPatientWorkspaceCarePathwayScreeningSearchParamsOptions = {
  /** Defaults to the matching sub-tab route (`/screening/care-pathways/matching/:tab`). */
  list?: PatientWorkspaceScreeningListReturn;
};

/** Matching-table filter snapshot for patient workspace URLs (Screen breadcrumb survives refresh). */
export function buildPatientWorkspaceCarePathwayScreeningSearchParams(
  filters: TIMEScreeningState['careGap']['filters'],
  options?: BuildPatientWorkspaceCarePathwayScreeningSearchParamsOptions,
): Record<string, string> {
  const list = options?.list ?? 'matching';
  const tab = filters.tab || MatchingTab.UNSCREENED;
  return encodePatientWorkspaceOrigin({
    kind: 'carePathwayScreening',
    list,
    matchingTab: list === 'matching' ? kebabCase(tab.toLowerCase()) : undefined,
    filterParams: storeToParams(filters),
  });
}

/** Prescreening queue to open after a care-pathway screen is saved. */
export function resolveCarePathwayScreeningQueueReturn(input: {
  /** Patient workspace search. Screening origin wins over the Redux matching-queue fallback. */
  search: string;
  breadcrumbUrl: string;
  queryString: string;
}): { pathname: string; search: string } {
  const origin = decodePatientWorkspaceOrigin(input.search);
  if (origin.kind === 'carePathwayScreening') {
    return getCarePathwayScreeningLocation(origin);
  }

  const rawSearch = input.queryString.trim();
  return {
    pathname: input.breadcrumbUrl,
    search: rawSearch ? (rawSearch.startsWith('?') ? rawSearch : `?${rawSearch}`) : '',
  };
}

export type NavigateToPatientWorkspaceFromCarePathwayRowOptions = {
  tab?: PATIENT_PAGE_TAB;
  extraSearchParams?: TimePrescreeningPageUrlOptions['searchParams'];
};

export function buildPatientWorkspaceUrlFromCarePathwayRow(
  row: Pick<PatientCarePathwayDtoType, 'id' | 'linkageId' | 'siteId'>,
  context: CarePathwayRowNavigationContext,
  tab: PATIENT_PAGE_TAB = PATIENT_PAGE_TAB.EVALUATION,
  extraSearchParams?: TimePrescreeningPageUrlOptions['searchParams'],
): string | null {
  const patientLinkageId = row.linkageId?.trim();
  const institutionId = resolveInstitutionIdForCarePathwayRow({ ...context, rowSiteId: row.siteId });

  if (!patientLinkageId || !institutionId) {
    return null;
  }

  return buildPatientWorkspaceUrl(patientLinkageId, institutionId, tab, {
    carePathwayEvaluationId: row.id,
    searchParams: {
      ...encodePatientWorkspaceOrigin({ kind: 'carePathwayHub' }),
      ...extraSearchParams,
    },
  });
}

export const PATIENT_WORKSPACE_NAV_UNAVAILABLE_MESSAGE =
  'Could not open this patient. Select a single site in the filter or global site selector, or open a row that includes site information.';

/** Opens the unified patient workspace when linkage + institution are known. */
export function navigateToPatientWorkspaceFromCarePathwayRow(
  row: Pick<PatientCarePathwayDtoType, 'id' | 'linkageId' | 'siteId'>,
  context: CarePathwayRowNavigationContext,
  navigate: NavigateFunction,
  options?: NavigateToPatientWorkspaceFromCarePathwayRowOptions,
): boolean {
  const tab = options?.tab ?? PATIENT_PAGE_TAB.EVALUATION;
  const unifiedUrl = buildPatientWorkspaceUrlFromCarePathwayRow(row, context, tab, options?.extraSearchParams);

  if (unifiedUrl) {
    navigate(unifiedUrl);
    return true;
  }

  return false;
}

/** Row click helper for matching/screened queues — invokes `onUnavailable` when institution cannot be resolved. */
export function tryNavigateToPatientWorkspaceFromCarePathwayRow(
  row: Pick<PatientCarePathwayDtoType, 'id' | 'linkageId' | 'siteId'>,
  context: CarePathwayRowNavigationContext,
  navigate: NavigateFunction,
  onUnavailable: () => void,
  options?: NavigateToPatientWorkspaceFromCarePathwayRowOptions,
): void {
  if (!navigateToPatientWorkspaceFromCarePathwayRow(row, context, navigate, options)) {
    onUnavailable();
  }
}
